import { WorkspaceMinimalFragment } from '@sketch/gql-types'

import {
  TreeNodeTypes,
  useTreeState,
} from '../../../projects/modals/ProjectPickerContent'
import { TreeState } from '../../../projects/modals/ProjectPickerContent/state.base'
import { isTruthy } from '@sketch/utils'
import { useEffect, useState } from 'react'

const findTargetWorkspace = (
  targetId: string,
  treeState: TreeState<TreeNodeTypes>
) => {
  const path = treeState.tree.getPathToNode(targetId)

  for (const pathId of path) {
    const node = treeState.tree.getNode(pathId)
    if (node?.payload?.__typename === 'Workspace') {
      return node.payload as WorkspaceMinimalFragment
    }
  }
}

export const getTargetLocation = (
  target: TreeNodeTypes,
  targetWorkspace: WorkspaceMinimalFragment | undefined
) => {
  const targetProject = target.__typename === 'Project' ? target : null

  if (!targetWorkspace) return null

  return { workspace: targetWorkspace, project: targetProject || null }
}

export const getLocations = (
  target: TreeNodeTypes,
  treeState: TreeState<TreeNodeTypes>
) => {
  const currentLocation = treeState.current
  const targetWorkspace = findTargetWorkspace(target.identifier, treeState)
  const targetLocation = getTargetLocation(target, targetWorkspace)

  const transferLocation =
    currentLocation && targetLocation
      ? { source: currentLocation, destination: targetLocation }
      : null

  return { transferLocation, targetWorkspace }
}

export interface PathSegment {
  id: string
  name: string
}
export const getPathToCurrent = (
  treeState: TreeState<TreeNodeTypes>
): PathSegment[] => {
  const currentNodeId = treeState.currentIds.currentId
  const pathIds = treeState.tree.getPathToNode(currentNodeId)

  return pathIds
    .map(x => {
      const payload = treeState.tree.getNode(x)?.payload
      if (!payload) return null
      return { id: payload.identifier, name: payload.name }
    })
    .filter(isTruthy)
}

export const usePathToCurrent = () => {
  const treeState = useTreeState()

  const [path, setPath] = useState<PathSegment[]>([])

  useEffect(() => {
    const currentNodeId = treeState.currentIds.currentId
    return treeState.registerUpdateListener(currentNodeId, () => {
      setPath(getPathToCurrent(treeState))
    })
  }, [treeState])

  return path
}

export function canAccessListChange(
  target: TreeNodeTypes,
  treeState: TreeState<TreeNodeTypes>
): boolean {
  const { transferLocation } = getLocations(target, treeState)

  if (!transferLocation) return false
  const { source, destination } = transferLocation

  if (source.workspace.identifier !== destination.workspace.identifier)
    return true

  const currentRootProjectId =
    source.project?.topLevelProject?.identifier ||
    source.project?.identifier ||
    null

  const destinationRootProjectId =
    destination.project?.topLevelProject?.identifier ||
    destination.project?.identifier ||
    null

  if (destinationRootProjectId !== currentRootProjectId) return true

  return false
}
